import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { combineLatest, exhaustMap, filter, from, of, switchMap, withLatestFrom } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { MeasureActions } from '../measure.actions';
import { BlockType } from '../../models/enums/create-block.type';
import { MeasureSurveyService } from '../../services/measure-survey.service';
import { MeasureBlockService } from '../../services/measure-block.service';
import { MeasureUtilService } from '../../services/measure-util.service';
import { selectCurrentSurveyVersionId, selectFlowTargetTypes, selectQuestionTypes } from '../measure.selector';
import { exhaustMapLatest } from '../../../../shared/utils/exhaust-map-latest';
import { Store } from '@ngrx/store';
import { MessageService } from 'primeng/api';
import { QuestionService } from '../../services/question.service';
import { defaultQuestionPlaceholdersConfig } from '../../config/default-question-placeholders.config';
import { SkipLogicSubExpressionsItemPostModel } from '../../models/api/builder-skip-logic-item.model';
import { FlowItemsService } from '../../services/flow-items.service';
import { SkipLogicService } from '../../services/skip-logic.service';
import { QuestionTypeEnum } from '../../models/enums/question.type';

@Injectable()
export class MeasureEffects {
  currentMeasureVersionId$ = this.store.select(selectCurrentSurveyVersionId);
  questionTypes$ = this.store.select(selectQuestionTypes);
  targetTypes$ = this.store.select(selectFlowTargetTypes);

  loadSurveyVersionQuestionTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.loadSurveyVersionQuestionTypes),
      // static reference data — load once, ignore repeated requests while one is in flight
      withLatestFrom(this.questionTypes$),
      filter(([, questionTypes]) => !questionTypes?.length),
      exhaustMap(() =>
        this.measureUtilService.getQuestionTypes().pipe(
          map((data) => MeasureActions.loadSurveyVersionQuestionTypesSuccess({ questionTypes: data.data })),
          catchError(() => of(MeasureActions.loadSurveyVersionQuestionTypesFailure())),
        ),
      ),
    ),
  );

  loadTargetTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.loadFlowTargetTypes),
      withLatestFrom(this.targetTypes$),
      filter(([, targetTypes]) => !targetTypes?.length),
      exhaustMap(() =>
        this.measureUtilService.getTargetTypes().pipe(
          map((data) => MeasureActions.loadFlowTargetTypesSuccess({ targetTypes: data.data })),
          catchError(() => of(MeasureActions.loadFlowTargetTypesFailure())),
        ),
      ),
    ),
  );

  createMeasureBlock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.createMeasureBlock),
      withLatestFrom(this.currentMeasureVersionId$),
      mergeMap(([action, surveyVersionId]) => {
        switch (action.blockType) {
          case BlockType.MATRIX: {
            return this.measureBlockService.createSurveyMatrixBlock(action.measureVersionId).pipe(
              map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
              catchError((error) => of(MeasureActions.createMeasureBlockFailure({ error: error.message }))),
            );
          }
          case BlockType.DEFAULT: {
            return this.measureBlockService.createSurveyDefaultBlock(action.measureVersionId, action.display).pipe(
              map((res: any) => MeasureActions.addDefaultScale({ blockId: res?.data?.[0]?.idobject || 0 })),
              catchError((error) => of(MeasureActions.createMeasureBlockFailure({ error: error.message }))),
            );
          }
          case BlockType.DEMOGRAPHICS: {
            return this.measureBlockService.createSurveyDemographicBlock(action.measureVersionId).pipe(
              map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
              catchError((error) => of(MeasureActions.createMeasureBlockFailure({ error: error.message }))),
            );
          }
          case BlockType.LIKERT: {
            return this.measureBlockService.createSurveyDefaultBlock(action.measureVersionId, action.display).pipe(
              map((res: any) => MeasureActions.createLikertScale({ blockId: res?.data?.[0]?.idobject || 0 })),
              catchError((error) => of(MeasureActions.createMeasureBlockFailure({ error: error.message }))),
            );
          }
          default: {
            return of(MeasureActions.createMeasureBlockFailure({ error: 'Unknown block type' }));
          }
        }
      }),
    ),
  );

  removeBlock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.removeBlock),
      withLatestFrom(this.currentMeasureVersionId$),
      mergeMap(([action, surveyVersionId]) =>
        this.measureBlockService.removeSurveyBlock(action.blockId).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError((error) => of(MeasureActions.removeBlockFailure({ error: error.message }))),
        ),
      ),
    ),
  );
  moveBlockUp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.moveBlockUp),
      withLatestFrom(this.currentMeasureVersionId$),
      mergeMap(([action, surveyVersionId]) =>
        this.measureBlockService.moveSurveyBlockUp(action.blockId).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError((error) => of(MeasureActions.moveBlockUpFailure({ error: error.message }))),
        ),
      ),
    ),
  );
  moveBlockDown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.moveBlockDown),
      withLatestFrom(this.currentMeasureVersionId$),
      mergeMap(([action, surveyVersionId]) =>
        this.measureBlockService.moveSurveyBlockDown(action.blockId).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError((error) => of(MeasureActions.moveBlockDownFailure({ error: error.message }))),
        ),
      ),
    ),
  );

  reorderBlock$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.reorderBlock),
      withLatestFrom(this.currentMeasureVersionId$),
      mergeMap(([action, surveyVersionId]) =>
        this.measureBlockService.updateBlockIndex(action.blockId, action.index).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError((error) => of(MeasureActions.reorderBlockFailure({ error: error.message }))),
        ),
      ),
    ),
  );

  updateBlockDisplay$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateBlockDisplay),
      withLatestFrom(this.currentMeasureVersionId$),
      mergeMap(([action, surveyVersionId]) =>
        this.measureBlockService.updateBlockDisplay(action.blockId, action.display).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError((error) => of(MeasureActions.updateBlockDisplayFailure({ error: error.message }))),
        ),
      ),
    ),
  );

  createMeasureBlockScale$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.addDefaultScale),
      mergeMap((action) =>
        this.measureBlockService.createScaleWithDefaultType(action.blockId).pipe(
          map((res: any) => MeasureActions.createMAVRQuestion({ scaleId: res?.data?.[0]?.idobject || 0 })),
          catchError((error) => of(MeasureActions.createMeasureBlockFailure({ error: error.message }))),
        ),
      ),
    ),
  );

  createLikertBlockScale$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.createLikertScale),
      mergeMap((action) =>
        this.measureBlockService.createScaleWithDefaultType(action.blockId).pipe(
          map((res: any) => MeasureActions.createLikertQuestion({ scaleId: res?.data?.[0]?.idobject || 0 })),
          catchError((error) => of(MeasureActions.createMeasureBlockFailure({ error: error.message }))),
        ),
      ),
    ),
  );

  createMAVRQuestion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.createMAVRQuestion),
      withLatestFrom(this.currentMeasureVersionId$, this.questionTypes$),
      mergeMap(([action, surveyVersionId, questionTypes]) => {
        const mavrType = questionTypes?.find((t) => t.name === QuestionTypeEnum.MC_MAVR);

        const request$ = mavrType
          ? this.questionService.addQuestion(
              action.scaleId,
              defaultQuestionPlaceholdersConfig.display,
              mavrType,
              action.index,
            )
          : this.questionService.createMAVRQuestion(action.scaleId, defaultQuestionPlaceholdersConfig.display);

        return request$.pipe(
          tap((result) => console.log('Added question:', result)),
          mergeMap(() =>
            from([
              MeasureActions.getSurveyVersionTree({ surveyVersionId }),
              MeasureActions.getAllSurveyVersionQuestions({ versionId: surveyVersionId }),
            ]),
          ),
          catchError((error) => of(MeasureActions.createMeasureBlockFailure({ error: error.message }))),
        );
      }),
    ),
  );

  createLikertQuestion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.createLikertQuestion),
      withLatestFrom(this.currentMeasureVersionId$),
      mergeMap(([action, surveyVersionId]) =>
        this.questionService.createLikertQuestion(action.scaleId).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError((error) => of(MeasureActions.createMeasureBlockFailure({ error: error.message }))),
        ),
      ),
    ),
  );

  createNPSLabel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.createNPSLabel),
      withLatestFrom(this.currentMeasureVersionId$),
      mergeMap(([action, surveyVersionId]) =>
        this.questionService.createNPSLabel(action.questionId, action.display, action.index).pipe(
          tap((result) => console.log('Added NPS Labels:', result)),
          mergeMap(() =>
            from([
              MeasureActions.getSurveyVersionTree({ surveyVersionId }),
              MeasureActions.getAllSurveyVersionQuestions({ versionId: surveyVersionId }),
            ]),
          ),
          catchError((error) => of(MeasureActions.createMeasureBlockFailure({ error: error.message }))),
        ),
      ),
    ),
  );

  getSurveyVersionTree$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.getSurveyVersionTree),
      // every mutation triggers a full reload — coalesce bursts instead of piling requests on the backend
      exhaustMapLatest((action) =>
        combineLatest([
          this.flowItemsService.getAllFlowItems(action.surveyVersionId),
          this.measureSurveyService.getSurveyVersionTree(action.surveyVersionId),
        ]).pipe(
          mergeMap(([flowItems, surveyVersionTree]) => [
            MeasureActions.loadSurveyVersionTree({ surveyVersionTree: surveyVersionTree.data[0] }),
            MeasureActions.getAllFlowItemsSuccess({ flowItems: flowItems.data }),
            MeasureActions.loadSurveyVersionTreeSuccess(),
          ]),
          catchError(() => of(MeasureActions.loadSurveyVersionTreeFailure())),
        ),
      ),
    ),
  );

  editSurveyMetaData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.editSurveyMetaData),
      // the settings tab saves on demand; the survey name/description do not affect the version tree
      switchMap((action) =>
        this.measureBlockService.updateSurveyMetaData(action.surveyId, action.display, action.description).pipe(
          tap(() =>
            this.messageService.add({
              severity: 'success',
              summary: 'Saved',
              detail: 'Measure settings saved',
            }),
          ),
          map(() =>
            MeasureActions.editSurveyMetaDataSuccess({ display: action.display, description: action.description }),
          ),
          // the error interceptor already shows the failure toast
          catchError(() => of(MeasureActions.editSurveyMetaDataFailure())),
        ),
      ),
    ),
  );

  updateSurvey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateSurvey),
      withLatestFrom(this.currentMeasureVersionId$),
      mergeMap(([action, surveyVersionId]) =>
        this.measureBlockService.publishingSurvey(action.versionId, action.surveyVersionActive).pipe(
          map((resultResponse) => {
            if (resultResponse.success) {
              return MeasureActions.updateCurrentSurveyForEdit({ surveyVersionActive: action.surveyVersionActive });
            } else {
              return MeasureActions.getSurveyVersionTree({ surveyVersionId });
            }
          }),
          catchError((error) => of(MeasureActions.updateSurveyFailure({ error: error.message }))),
        ),
      ),
    ),
  );

  createSkipLogicItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.createSkipLogicItem),
      withLatestFrom(this.currentMeasureVersionId$),
      mergeMap(([action, surveyVersionId]) => {
        return this.skipLogicService.createSkipLogicItem(action.questionOid, { itemTarget: action.targetType }).pipe(
          switchMap((itemTargetResponse) => {
            if (!itemTargetResponse.success) {
              catchError((error) => of(MeasureActions.createSkipLogicItemFailure({ error: error.message })));
            }
            const idObject = itemTargetResponse.data[0].idobject;
            if (!idObject) {
              catchError((error) => of(MeasureActions.createSkipLogicItemFailure({ error: error.message })));
            }

            const expressionData = { type: action.expressionTypeOid };
            return this.skipLogicService.createSkipLogicExpressions(idObject, expressionData);
          }),
          switchMap((expressionsResponse) => {
            const idObject = expressionsResponse.data[0]?.idobject;
            if (!idObject) {
              catchError((error) => of(MeasureActions.createSkipLogicItemFailure({ error: error.message })));
            }

            const subExpressionData: SkipLogicSubExpressionsItemPostModel = {
              // text-entry choices are addressed via ChoiceTextEntryValue (used with unary Is Empty / Is Not Empty)
              leftOperand: `q://${action.questionName}/${action.choiceIsTextEntry ? 'ChoiceTextEntryValue' : 'SelectableChoice'}/${action.choice}`,
              operator: action.operator,
              logicType: action.logicType,
            };

            return this.skipLogicService.createSubExpression(idObject, subExpressionData);
          }),
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError((error) => of(MeasureActions.createSkipLogicItemFailure({ error: error.message }))),
        );
      }),
    ),
  );
  updateSkipLogicItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.updateSkipLogicItem),
      withLatestFrom(this.currentMeasureVersionId$),
      mergeMap(([action, surveyVersionId]) => {
        return this.skipLogicService
          .updateSkipLogicItem(action.itemData.idobject, {
            idobject: action.itemData.idobject,
            itemTarget: action.targetType,
          })
          .pipe(
            switchMap((expressionsResponse) => {
              const subExpressionData: SkipLogicSubExpressionsItemPostModel = {
                idobject: action.itemData.expressions[0].subExpressions![0].idobject,
                // text-entry choices are addressed via ChoiceTextEntryValue (used with unary Is Empty / Is Not Empty)
                leftOperand: `q://${action.questionName}/${action.choiceIsTextEntry ? 'ChoiceTextEntryValue' : 'SelectableChoice'}/${action.choice}`,
                operator: action.operator,
                logicType: action.logicType,
              };

              return this.skipLogicService.updateSubExpression(
                action.itemData.expressions[0].subExpressions![0].idobject,
                subExpressionData,
              );
            }),
            map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
            catchError((error) => of(MeasureActions.updateSkipLogicItemFailure({ error: error.message }))),
          );
      }),
    ),
  );
  removeSkipLogicItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MeasureActions.removeSkipLogicItem),
      withLatestFrom(this.currentMeasureVersionId$),
      mergeMap(([action, surveyVersionId]) =>
        this.skipLogicService.removeSkipLogicItem(action.skipLogicItemsOid).pipe(
          map(() => MeasureActions.getSurveyVersionTree({ surveyVersionId })),
          catchError((error) => of(MeasureActions.removeBlockFailure({ error: error.message }))),
        ),
      ),
    ),
  );

  constructor(
    private store: Store,
    private actions$: Actions,
    private measureSurveyService: MeasureSurveyService,
    private flowItemsService: FlowItemsService,
    private measureBlockService: MeasureBlockService,
    private measureUtilService: MeasureUtilService,
    private questionService: QuestionService,
    private skipLogicService: SkipLogicService,
    private messageService: MessageService,
  ) {}
}
